/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';

import NavigationService from 'services/NavigationService';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import * as accountPermissions from 'permissions/account';
import * as generalPermissions from 'permissions/general';
import * as reportingPermissions from 'permissions/reporting';
import TableButton from 'components/TableButton';
import { actionDrawerStore } from 'services/ServiceRegistry';
import { formulateUri } from 'tables/components/ReportView/state';
import RegionStore from 'stores/RegionStore';

import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfigGetter } from '../components/ReportView/ReportViewBase';
import type {
  ViewConfig,
  CellComponentProps,
  DataViewColumn,
} from '../components/ReportView/types';
import type { ReportViewState } from '../components/ReportView/state';

const columns: DataViewColumn[] = [
  {
    columnName: 'account_no',
    title: 'general:reporting.brand.labels.account_number',
    flexBasis: 140,
    noFlexGrow: true,
    component: function AccountNameCell({ display, rowData }) {
      return (
        <Link to={`/reporting/brand/${rowData.brand_id}/account/${rowData.account_id}`}>
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'account_name',
    title: 'general:reporting.brand.labels.name',
    flexBasis: 300,
    component: function AccountNameCell({ display, rowData }) {
      return (
        <Link to={`/reporting/brand/${rowData.brand_id}/account/${rowData.account_id}`}>
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'region_name',
    title: 'general:reporting.brand.labels.region',
    flexBasis: 120,
    noFlexGrow: true,
    component: function RegionNameComponent({ display, t }) {
      return t(`general:regions.${display}`);
    },
  },
  {
    columnName: 'er_contracts_open_count',
    title: 'general:reporting.brand.labels.open_contracts',
    noFlexGrow: true,
    flexBasis: 120,
    component: function ContractsOpenCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/orders', {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.account_id] },
              { key: 'er_status', values: ['open'] },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_contracts_closed_count',
    title: 'general:reporting.brand.labels.closed_contracts',
    noFlexGrow: true,
    flexBasis: 120,
    component: function ContractsClosedCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/orders', {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.account_id] },
              { key: 'er_status', values: ['closed'], overrideImplicit: true },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_assets_total_rental_count',
    title: 'general:reporting.brand.labels.rental_assets',
    noFlexGrow: true,
    flexBasis: 120,
    component: function AssetsRentalCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.account_id] },
              { key: 'asset_type', values: ['Rented Open'] },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_assets_on_sold_count',
    title: 'general:reporting.brand.labels.sale_assets',
    noFlexGrow: true,
    flexBasis: 120,
    component: function AssetsSaleCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.account_id] },
              { key: 'asset_type', values: ['Purchased'] },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_assets_owned_count',
    title: 'general:reporting.brand.labels.owned_assets',
    noFlexGrow: true,
    flexBasis: 120,
    component: function AssetsOwnedCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.account_id] },
              { key: 'asset_type', values: ['Owned'], overrideImplicit: true },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_account_projects_count',
    title: 'general:reporting.brand.labels.projects',
    noFlexGrow: true,
    flexBasis: 120,
    component: function ProjectsCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/projects', {
            page: 1,
            filters: [{ key: 'account', values: [rowData.account_id] }],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_account_cost_centres_count',
    title: 'general:reporting.brand.labels.cost_centres',
    noFlexGrow: true,
    flexBasis: 120,
    component: function CostCentresCountCell({ display, rowData }) {
      return (
        <Link
          to={formulateUri('/cost-centers', {
            page: 1,
            filters: [{ key: 'account', values: [rowData.account_id] }],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_account_users_count',
    title: 'general:reporting.brand.labels.users',
    noFlexGrow: true,
    flexBasis: 120,
    component: function UsersCountCell({ display, rowData }) {
      return (
        <Link to={{ pathname: `/accounts/${rowData.account_id}`, state: { tab: 1 } }}>
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'er_account_users_unique_login_count',
    title: 'general:reporting.brands.labels.unique_user_logins',
    noFlexGrow: true,
    flexBasis: 120,
  },
  {
    columnName: 'account_id',
    title: '',
    flexBasis: 100,
    noFlexGrow: true,
    component: function DetailNavCell({ display }) {
      const handleDetailButtonClick = () => {
        NavigationService.navigateTo(`/accounts/${display}`);
      };

      return (
        <TableButton label="Detail" iconHref="#icon-list-alt" onClick={handleDetailButtonClick} />
      );
    },
  },
];

const getViewConfig: ViewConfigGetter = (props, state, getters) => ({
  title: 'general:reporting.brand.title',
  codename: 'reporting-brand',
  type: 'SINGLE_WITH_SUMMARY',
  userFilterNamespace: 'Accounts',
  disableFilters: true,
  disableUserPreferenceSave: true,
  disableFilterChips: true,
  downloadPermissions: reportingPermissions.downloadAccounts,
  defaultFilters: props.userStore.userLoaded
    ? [
        {
          key: RegionStore.REGION_FILTER_KEY,
          values: [props.regionStore.regionOptionsInfo.suggestedDefault],
          fixed: true,
        },
      ]
    : undefined,
  view: {
    dataPathname: 'account-stats',
    apiType: 'SQL',
    columns,
  },
  summaryView: {
    title: {
      dependencies: ['routing', 'brandStore'],
      compute: data => [
        {
          text: 'general:reporting.brands.summary.title',
          to: formulateUri('/reporting', {
            page: 1,
            filters: [
              (state.filters || []).find(item => item.key === RegionStore.REGION_FILTER_KEY),
            ].filter(item => !!item),
          }),
        },
        {
          text:
            data.brandStore && !data.brandStore.brandLoading && data.brandStore.selectedBrand
              ? data.brandStore.selectedBrand.name
              : 'Loading...',
          loading: !data.brandStore || data.brandStore.brandLoading,
        },
      ],
    },
    sectionHeading: 'general:reporting.brand.summary.section_heading',
    dataPathname: 'brand-stats',
    enableRegionSelect: true,
    useUriTokens: ['brandId'],
    sectionHeadingButtons: [
      {
        label: 'general:actions.export_summary',
        icon: '#icon-download-page',
        onClick: () => {
          const { brandId, region } = getters.getSummaryViewInfo();

          props.reportViewStore.exportSummary('brand-stats', { region, brandId });
        },
        permissions: reportingPermissions.viewAllBrands,
      },
    ],
    logoPath: ['props', 'brandStore', 'selectedBrand', 'logo'],
    mapDataToLayout: data => {
      let targetItem = {};
      const allItem = data.find(item => item.region_id === props.regionStore.allRegionId);

      if (allItem) {
        targetItem = allItem;
        targetItem.regionalData = data.reduce((_regionalData, item) => {
          if (item.region_id !== props.regionStore.allRegionId) {
            _regionalData[item.region_id] = item;
          }

          return _regionalData;
        }, {});
      } else {
        [targetItem = {}] = data;
      }

      const {
        trading_accounts_count,
        er_contracts_open_count,
        er_contracts_closed_count,
        er_assets_total_rental_count,
        er_assets_on_sold_count,
        er_assets_owned_count,
        brand_unique_users_count,
        er_account_projects_count,
        er_account_projects_current_year_count,
        er_account_projects_current_month_count,
        er_account_cost_centres_count,
        er_account_cost_centres_current_year_count,
        er_account_cost_centres_current_month_count,
        er_account_users_unique_login_count,
        er_account_users_login_count,
        er_account_users_current_month_unique_login_count,
        er_account_users_current_year_unique_login_count,
        er_account_users_current_month_login_count,
        er_account_users_current_year_login_count,

        request_contract_extension_count,
        request_contract_extension_current_month_count,
        request_contract_extension_current_year_count,

        request_asset_rental_extension_count,
        request_asset_rental_extension_current_month_count,
        request_asset_rental_extension_current_year_count,

        quote_request_count,
        quote_request_current_month_count,
        quote_request_current_year_count,

        new_asset_request_count,
        new_asset_request_current_month_count,
        new_asset_request_current_year_count,

        new_license_request_count,
        new_license_request_current_year_count,
        new_license_request_current_month_count,

        asset_calibration_request_count,
        asset_calibration_request_current_month_count,
        asset_calibration_request_current_year_count,

        asset_fault_count,
        asset_fault_current_month_count,
        asset_fault_current_year_count,

        unique_users_count,
      } = targetItem;

      const layout = [
        [
          {
            title: 'general:reporting.brand.summary.main_card_title',
            columns: [{ heading: 'general:reporting.summary_titles.summary' }],
            data: [
              {
                label: 'general:reporting.summary_labels.trading_accounts',
                data: [trading_accounts_count],
              },
              {
                label: 'general:reporting.summary_labels.open_contracts',
                data: [er_contracts_open_count],
              },
              {
                label: 'general:reporting.summary_labels.closed_contracts',
                data: [er_contracts_closed_count],
              },
              {
                label: 'general:reporting.summary_labels.rental_assets',
                data: [er_assets_total_rental_count],
              },
              {
                label: 'general:reporting.summary_labels.sale_assets',
                data: [er_assets_on_sold_count],
              },
              {
                label: 'general:reporting.summary_labels.owned_assets',
                data: [er_assets_owned_count],
              },
            ],
          },
        ],
        [
          ...(allItem && !props.regionStore.regionsLoading
            ? [
                {
                  title: 'general:reporting.summary_titles.trading_accounts',
                  columns: Object.keys(targetItem.regionalData).map(regionId => ({
                    heading: `general:regions.${props.regionStore.regions[regionId] &&
                      props.regionStore.regions[regionId].name}`,
                  })),
                  data: [
                    {
                      label: 'general:reporting.summary_labels.by_region',
                      data: Object.keys(targetItem.regionalData).map(regionId =>
                        targetItem.regionalData[regionId]
                          ? targetItem.regionalData[regionId].trading_accounts_count
                          : 'N/A'
                      ),
                    },
                  ],
                },
              ]
            : []),
          {
            title: 'general:reporting.summary_titles.usage',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.users',
                data: [brand_unique_users_count, '-', '-'],
              },
              {
                label: 'general:reporting.summary_labels.users_login',
                data: [
                  er_account_users_login_count,
                  er_account_users_current_year_login_count,
                  er_account_users_current_month_login_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.users_unique_login',
                data: [
                  er_account_users_unique_login_count,
                  er_account_users_current_year_unique_login_count,
                  er_account_users_current_month_unique_login_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.process_requests',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.asset_request_submissions',
                data: [
                  new_asset_request_count,
                  new_asset_request_current_year_count,
                  new_asset_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.license_request_submissions',
                data: [
                  new_license_request_count,
                  new_license_request_current_year_count,
                  new_license_request_current_month_count,
                ],
              },
            ],
          },
        ],
        [
          {
            title: 'general:reporting.summary_titles.customer_created_content',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.projects',
                data: [
                  er_account_projects_count,
                  er_account_projects_current_year_count,
                  er_account_projects_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.cost_centres',
                data: [
                  er_account_cost_centres_count,
                  er_account_cost_centres_current_year_count,
                  er_account_cost_centres_current_month_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.asset_activity',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.contract_extensions',
                data: [
                  request_contract_extension_count,
                  request_contract_extension_current_year_count,
                  request_contract_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.asset_extensions',
                data: [
                  request_asset_rental_extension_count,
                  request_asset_rental_extension_current_year_count,
                  request_asset_rental_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.quote_requests',
                data: [
                  quote_request_count,
                  quote_request_current_year_count,
                  quote_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.calibration_requests',
                data: [
                  asset_calibration_request_count,
                  asset_calibration_request_current_year_count,
                  asset_calibration_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.faults_reported',
                data: [
                  asset_fault_count,
                  asset_fault_current_year_count,
                  asset_fault_current_month_count,
                ],
              },
            ],
          },
        ],
      ];

      if (allItem) {
        layout[0].unshift();
      }

      return layout;
    },
  },
  buttons: [
    {
      label: 'New',
      icon: '#icon-add',
      to: '/accounts/create',
      permissions: accountPermissions.create,
    },
  ],
});

export default createReportView(getViewConfig);
